'use client';

import { useEffect, useId, useRef } from 'react';
import {
  FLAPS,
  START,
  XS,
  easeTear,
  flapOpen,
  flapPoly,
  introYs,
  makeTear,
  morphYs,
  polyD,
  rng,
  sheetD,
  type Flap,
  type Tear,
} from './footer-paper-geometry';

const INITIAL_WIDTH = 1000;
const INITIAL_HEIGHT = 210;
const INITIAL_TEAR = makeTear(23);
const FOLD_SLOTS = Array.from({ length: FLAPS * 2 }, (_, index) => index);

const clamp = (value: number, min = 0, max = 1) => Math.min(max, Math.max(min, value));
const easeInOut = (value: number) => {
  const t = clamp(value);
  return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
};

function initialFold(index: number) {
  const flap = INITIAL_TEAR.flaps[index];
  if (!flap) return { path: '', x1: 0, y1: 0, x2: 0, y2: 0 };
  const points = flapPoly(INITIAL_TEAR.ys, flap, INITIAL_WIDTH, INITIAL_HEIGHT, 1);
  const end = Math.min(INITIAL_TEAR.ys.length - 1, flap.at + flap.span);
  const tip = points[flap.span + 3];
  return {
    path: polyD(points),
    x1: ((XS[flap.at] + XS[end]) / 2) * INITIAL_WIDTH,
    y1: ((INITIAL_TEAR.ys[flap.at] + INITIAL_TEAR.ys[end]) / 2) * INITIAL_HEIGHT,
    x2: tip[0],
    y2: tip[1],
  };
}

const INITIAL_FOLDS = FOLD_SLOTS.map(initialFold);

/** The approved paper edge; animation never owns or hides the footer's links. */
export function FooterPaperEdge() {
  const id = `footer-paper-${useId().replace(/[^a-zA-Z0-9_-]/g, '')}`;
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const surface = rootRef.current;
    if (!surface) return;
    const button = surface.querySelector<HTMLButtonElement>('.tear-seal');
    const svg = surface.querySelector<SVGSVGElement>('.tear-stage');
    const sheet = surface.querySelector<SVGPathElement>('.tear-sheet-path');
    const rim = surface.querySelector<SVGPathElement>('.tear-fibre-path');
    const noise = surface.querySelector<SVGImageElement>('.tear-noise-image');
    if (!button || !svg || !sheet || !rim || !noise) return;

    const flaps = [...surface.querySelectorAll<SVGPathElement>('.tear-fold')];
    const gradients = [
      ...surface.querySelectorAll<SVGLinearGradientElement>('.tear-fold-gradient'),
    ];
    const motion = window.matchMedia('(prefers-reduced-motion: reduce)');
    let reduced = motion.matches;
    let visible = false;
    let torn = reduced;
    let disposed = false;
    let grainReady = false;
    let frameId = 0;
    let introTimer = 0;
    let width = surface.clientWidth;
    let height = surface.clientHeight;
    let seed = 23;
    let mode: 'wait' | 'intro' | 'morph' | 'rest' = reduced ? 'rest' : 'wait';
    let startedAt = 0;
    let duration = 1550;
    let tear = makeTear(seed);
    let previous: Tear | null = null;
    let ys = tear.ys;
    let from: number[] = [];
    let pointerX = -9999;
    let pointerY = -9999;
    const lifts: number[] = Array(FLAPS).fill(0);

    const canAnimate = () => !disposed && visible && !document.hidden && !reduced;
    const requestFrame = () => {
      if (!frameId && canAnimate()) {
        frameId = window.requestAnimationFrame((now) => {
          frameId = 0;
          paint(now);
        });
      }
    };

    function paint(now: number) {
      if (disposed || !width || !height) return;
      let busy = false;
      const open: number[] = Array(FLAPS).fill(1);
      const old: number[] = Array(FLAPS).fill(0);
      const progress = clamp((now - startedAt) / duration);

      if (mode === 'wait') {
        ys = tear.ys.map(() => START);
        open.fill(0);
      } else if (mode === 'intro') {
        ys = introYs(tear, progress);
        tear.flaps.forEach((flap, index) => {
          open[index] = flapOpen(tear, flap, progress);
        });
        busy = progress < 1;
        if (!busy) mode = 'rest';
      } else if (mode === 'morph') {
        ys = morphYs(from, tear, progress);
        open.fill(easeTear((progress - 0.5) / 0.5));
        old.fill(1 - easeInOut(progress / 0.35));
        busy = progress < 1;
        if (!busy) mode = 'rest';
      } else {
        ys = tear.ys;
      }

      tear.flaps.forEach((flap, index) => {
        const end = Math.min(ys.length - 1, flap.at + flap.span);
        const x = ((XS[flap.at] + XS[end]) / 2) * width;
        const y = ((ys[flap.at] + ys[end]) / 2) * height;
        const target = reduced
          ? 0
          : clamp(1 - Math.hypot(pointerX - x, pointerY - y) / (0.2 * Math.max(width, 520)));
        lifts[index] += (target - lifts[index]) * 0.14;
        if (Math.abs(target - lifts[index]) > 0.003) busy = true;
        open[index] *= 1 + 0.32 * lifts[index];
      });

      sheet!.setAttribute('d', sheetD(ys, width, height, tear.rim));
      rim!.setAttribute('d', sheetD(ys, width, height));
      const drawFold = (index: number, flap: Flap | undefined, amount: number) => {
        const path = flaps[index];
        const gradient = gradients[index];
        if (!path || !gradient) return;
        if (!flap || amount < 0.01) {
          path.setAttribute('d', '');
          return;
        }
        const points = flapPoly(ys, flap, width, height, amount);
        const end = Math.min(ys.length - 1, flap.at + flap.span);
        const tip = points[flap.span + 3];
        path.setAttribute('d', polyD(points));
        gradient.setAttribute('x1', String(((XS[flap.at] + XS[end]) / 2) * width));
        gradient.setAttribute('y1', String(((ys[flap.at] + ys[end]) / 2) * height));
        gradient.setAttribute('x2', String(tip[0]));
        gradient.setAttribute('y2', String(tip[1]));
      };
      for (let index = 0; index < FLAPS; index++) {
        drawFold(index, tear.flaps[index], open[index]);
        drawFold(FLAPS + index, previous?.flaps[index], old[index]);
      }
      button!.setAttribute('aria-disabled', String(mode !== 'rest'));
      if (busy) requestFrame();
    }

    const ensureGrain = () => {
      if (grainReady) return;
      grainReady = true;
      const canvas = document.createElement('canvas');
      canvas.width = canvas.height = 160;
      const context = canvas.getContext('2d');
      if (!context) return;
      const data = context.createImageData(160, 160);
      const random = rng(42);
      for (let index = 0; index < data.data.length; index += 4) {
        const value = random() < 0.5 ? 0 : 255;
        data.data[index] = data.data[index + 1] = data.data[index + 2] = value;
        data.data[index + 3] = Math.floor(random() * 46);
      }
      context.putImageData(data, 0, 0);
      noise.setAttribute('href', canvas.toDataURL());
    };

    const clearScheduled = () => {
      window.cancelAnimationFrame(frameId);
      window.clearTimeout(introTimer);
      frameId = introTimer = 0;
    };

    const settle = () => {
      clearScheduled();
      pointerX = pointerY = -9999;
      lifts.fill(0);
      button.style.setProperty('--tear-tilt', '0deg');
      surface.classList.remove('tear-replay');
      if (mode !== 'wait' || reduced) {
        mode = 'rest';
        torn = true;
        surface.classList.add('tear-visible');
      }
      paint(performance.now());
    };

    const start = () => {
      introTimer = 0;
      if (torn || disposed || !visible || document.hidden) return;
      torn = true;
      surface.classList.add('tear-visible');
      mode = reduced ? 'rest' : 'intro';
      startedAt = performance.now();
      paint(startedAt);
    };

    const resume = () => {
      if (!visible || document.hidden || disposed) return;
      ensureGrain();
      if (!torn && !introTimer) {
        if (reduced) start();
        else introTimer = window.setTimeout(start, 280);
      } else {
        paint(performance.now());
      }
    };

    const resize = new ResizeObserver(() => {
      window.cancelAnimationFrame(frameId);
      frameId = 0;
      width = surface.clientWidth;
      height = surface.clientHeight;
      svg.setAttribute('viewBox', `0 0 ${width} ${height}`);
      paint(performance.now());
    });
    resize.observe(surface);

    const intersection = new IntersectionObserver(
      ([entry]) => {
        visible = entry.isIntersecting && entry.intersectionRatio >= 0.18;
        if (visible) resume();
        else settle();
      },
      { threshold: [0, 0.18] }
    );
    intersection.observe(surface);

    const onPointerMove = (event: PointerEvent) => {
      if (!canAnimate() || event.pointerType === 'touch') return;
      const bounds = surface.getBoundingClientRect();
      pointerX = event.clientX - bounds.left;
      pointerY = event.clientY - bounds.top;
      button.style.setProperty('--tear-tilt', `${clamp((pointerX / width - 0.5) * 8, -4, 4)}deg`);
      requestFrame();
    };
    const onPointerLeave = () => {
      pointerX = pointerY = -9999;
      button.style.setProperty('--tear-tilt', '0deg');
      requestFrame();
    };
    const onRepeat = () => {
      if (mode !== 'rest' || !visible || document.hidden) return;
      previous = tear;
      from = [...ys];
      tear = makeTear(++seed);
      mode = reduced ? 'rest' : 'morph';
      startedAt = performance.now();
      duration = 1150;
      surface.classList.remove('tear-replay');
      if (!reduced) {
        void button.offsetWidth;
        surface.classList.add('tear-replay');
      }
      paint(startedAt);
    };
    const onMotionChange = () => {
      reduced = motion.matches;
      if (reduced) settle();
      else resume();
    };
    const onVisibilityChange = () => {
      if (document.hidden) settle();
      else resume();
    };

    surface.addEventListener('pointermove', onPointerMove, { passive: true });
    surface.addEventListener('pointerleave', onPointerLeave);
    button.addEventListener('click', onRepeat);
    motion.addEventListener('change', onMotionChange);
    document.addEventListener('visibilitychange', onVisibilityChange);

    return () => {
      disposed = true;
      clearScheduled();
      resize.disconnect();
      intersection.disconnect();
      surface.removeEventListener('pointermove', onPointerMove);
      surface.removeEventListener('pointerleave', onPointerLeave);
      button.removeEventListener('click', onRepeat);
      motion.removeEventListener('change', onMotionChange);
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, []);

  return (
    <div ref={rootRef} className="tear-edge">
      <svg
        className="tear-stage"
        viewBox={`0 0 ${INITIAL_WIDTH} ${INITIAL_HEIGHT}`}
        preserveAspectRatio="none"
        aria-hidden="true"
        focusable="false"
      >
        <defs>
          <linearGradient id={`${id}-light`} x1="0" y1="0" x2=".4" y2="1">
            <stop offset="0" stopColor="#fff" stopOpacity="0" />
            <stop offset=".6" stopColor="#fff" stopOpacity="0" />
            <stop offset="1" stopColor="#1c073d" stopOpacity=".1" />
          </linearGradient>
          <linearGradient id={`${id}-grain-fade`} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0" stopColor="black" />
            <stop offset=".5" stopColor="white" />
          </linearGradient>
          <mask id={`${id}-grain-mask`}>
            <rect width="100%" height="100%" fill={`url(#${id}-grain-fade)`} />
          </mask>
          <filter id={`${id}-drop`} x="-10%" y="-10%" width="120%" height="160%">
            <feDropShadow
              dx="0"
              dy="4"
              stdDeviation="3.5"
              floodColor="#250a42"
              floodOpacity=".25"
            />
          </filter>
          <filter id={`${id}-fold-shadow`} x="-50%" y="-50%" width="200%" height="220%">
            <feDropShadow dx="-2" dy="4" stdDeviation="3" floodColor="#250a42" floodOpacity=".23" />
          </filter>
          <pattern id={`${id}-grain`} width="160" height="160" patternUnits="userSpaceOnUse">
            <image className="tear-noise-image" width="160" height="160" />
          </pattern>
          {INITIAL_FOLDS.map((fold, index) => (
            <linearGradient
              key={index}
              className="tear-fold-gradient"
              id={`${id}-fold-${index}`}
              gradientUnits="userSpaceOnUse"
              x1={fold.x1}
              y1={fold.y1}
              x2={fold.x2}
              y2={fold.y2}
            >
              <stop offset="0" stopColor="#b8adc5" />
              <stop offset=".5" stopColor="#e0d9e8" />
              <stop offset="1" stopColor="#fffcf5" />
            </linearGradient>
          ))}
          <path
            id={`${id}-sheet`}
            className="tear-sheet-path"
            d={sheetD(INITIAL_TEAR.ys, INITIAL_WIDTH, INITIAL_HEIGHT, INITIAL_TEAR.rim)}
          />
        </defs>
        <rect className="tear-paper" width="100%" height="100%" />
        <g className="tear-svg-sheet" filter={`url(#${id}-drop)`}>
          <path
            className="tear-fibre-path"
            d={sheetD(INITIAL_TEAR.ys, INITIAL_WIDTH, INITIAL_HEIGHT)}
          />
          <use className="tear-purple-paper" href={`#${id}-sheet`} />
        </g>
        <use href={`#${id}-sheet`} fill={`url(#${id}-light)`} />
        <use
          href={`#${id}-sheet`}
          fill={`url(#${id}-grain)`}
          opacity=".2"
          mask={`url(#${id}-grain-mask)`}
        />
        <g filter={`url(#${id}-fold-shadow)`}>
          {INITIAL_FOLDS.map((fold, index) => (
            <path
              key={index}
              className="tear-fold"
              fill={`url(#${id}-fold-${index})`}
              d={fold.path}
            />
          ))}
        </g>
      </svg>
      <button
        className="tear-seal"
        type="button"
        aria-label="Оторвать бумагу ещё раз"
        title="Оторвать ещё раз"
      >
        <span className="tear-seal-tab" aria-hidden="true" />
        <span className="tear-seal-face" aria-hidden="true">
          <span className="tear-seal-emblem" />
        </span>
      </button>
    </div>
  );
}
